/**
 * main-product-v8.js
 *
 * Product V8 is a single-product PDP section. The product/template defines
 * the audience, flavor cards select real variants, and selling_plan_offer
 * blocks define subscription offers for that product.
 */

(function () {
  var scriptTag = document.currentScript;
  var sectionId = scriptTag ? scriptTag.dataset.id : null;

  function getSection() {
    return sectionId ? document.getElementById(sectionId) : document;
  }

  function fmtMoney(cents) {
    if (cents == null) return '';
    var val = cents / 100;
    return '$' + (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2));
  }

  function parseProductJSON(section) {
    var el = section.querySelector('.productV8JSON');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function getActiveOffer(section) {
    var select = section.querySelector('.productV8OfferSelect');
    if (!select) return null;
    return select.selectedOptions[0] || select.options[0] || null;
  }

  function getPlanAllocation(variant, offer) {
    if (!variant || !offer || !variant.selling_plan_allocations) return null;
    var planId = String(offer.value || '');
    return variant.selling_plan_allocations.find(function (allocation) {
      return String(allocation.selling_plan_id) === planId;
    }) || null;
  }

  function setText(el, value) {
    if (el) el.textContent = value || '';
  }

  function setHtml(el, value) {
    if (el) el.innerHTML = value || '';
  }

  function initGallerySwiper(section) {
    if (typeof Swiper === 'undefined') return;
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      var swiperEl = el.querySelector('.productVisualSwiper');
      if (!swiperEl) return;

      var instance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        autoHeight: true,
        pagination: {
          el: swiperEl.querySelector('.swiper-pagination'),
          clickable: true,
        },
        on: {
          slideChange: function () {
            var thumbs = el.querySelectorAll('.productVisualThumbsSlide');
            thumbs.forEach(function (thumb) { thumb.classList.remove('active'); });
            if (thumbs[this.realIndex]) thumbs[this.realIndex].classList.add('active');
          }
        }
      });

      el.querySelectorAll('.productVisualThumbsSlide').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          instance.slideTo(parseInt(this.dataset.index, 10) - 1);
        });
      });
    });
  }

  function switchGallery(section, id) {
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      el.classList.toggle('active', el.dataset.id === id);
    });
  }

  function resolveVariant(productData, flavorValue) {
    if (!productData || !productData.variants) return null;
    return productData.variants.find(function (variant) {
      return variant.options.indexOf(flavorValue) !== -1;
    }) || null;
  }

  function applyOfferAvailability(section, variant) {
    var select = section.querySelector('.productV8OfferSelect');
    if (!select) return;

    var allocations = variant && variant.selling_plan_allocations ? variant.selling_plan_allocations : [];
    var availablePlanIds = allocations.map(function (allocation) {
      return String(allocation.selling_plan_id);
    });
    if (!availablePlanIds.length) return;

    var needsReselect = false;
    Array.prototype.forEach.call(select.options, function (option) {
      var isAvailable = availablePlanIds.indexOf(String(option.value)) !== -1;
      option.disabled = !isAvailable;
      if (!isAvailable && option.selected) {
        option.selected = false;
        needsReselect = true;
      }
    });

    if (needsReselect) {
      var firstAvailable = Array.prototype.find.call(select.options, function (option) {
        return !option.disabled;
      });
      if (firstAvailable) firstAvailable.selected = true;
    }
  }

  function applyOfferTexts(section, variant) {
    var offer = getActiveOffer(section);
    var isSub = section.querySelector('.productV8PurchaseRadio[value="subscription"]:checked') !== null;
    var allocation = getPlanAllocation(variant, offer);

    var subPriceText = offer ? (offer.dataset.subscriptionPriceText || '') : '';
    var subCadence = offer ? (offer.dataset.subscriptionCadence || '') : '';
    var otpPriceText = offer ? (offer.dataset.otpPriceText || '') : '';
    var discountLabel = offer ? (offer.dataset.discountLabel || '') : '';
    var deliveryText = offer ? (offer.dataset.deliveryText || '') : '';
    var servingsDescription = offer ? (offer.dataset.servingsDescription || '') : '';
    var servingsPer = offer ? (offer.dataset.servingsPer || '') : '';
    var otpSupplyText = offer ? (offer.dataset.otpSupplyText || '') : '';

    if (!subPriceText) subPriceText = allocation ? fmtMoney(allocation.price) : fmtMoney(variant && variant.price);
    if (!otpPriceText) otpPriceText = fmtMoney(variant && variant.price);

    var mainPriceEl = section.querySelector('.productV8Price');
    if (mainPriceEl) mainPriceEl.textContent = isSub ? subPriceText : otpPriceText;

    var pricePerEl = section.querySelector('.productV8PricePer');
    if (pricePerEl) {
      if (isSub && subCadence) {
        pricePerEl.textContent = '/' + subCadence;
        pricePerEl.style.display = '';
      } else {
        pricePerEl.style.display = 'none';
      }
    }

    var subPriceEl = section.querySelector('.productV8SubPrice');
    if (subPriceEl) {
      subPriceEl.textContent = subPriceText;
    }

    // v8: cadence and OTP compare live in dedicated spans next to .productV8SubPrice
    var subBoxPricePerEl = section.querySelector('.productV8SubBoxPricePer');
    if (subBoxPricePerEl) {
      if (subCadence) {
        subBoxPricePerEl.textContent = '/' + subCadence;
        subBoxPricePerEl.style.display = '';
      } else {
        subBoxPricePerEl.style.display = 'none';
      }
    }
    var subBoxCompareEl = section.querySelector('.productV8SubBoxComparePrice');
    if (subBoxCompareEl) {
      if (otpPriceText) {
        subBoxCompareEl.textContent = otpPriceText;
        subBoxCompareEl.style.display = '';
      } else {
        subBoxCompareEl.style.display = 'none';
      }
    }
    // v8: plan title + servings line in the offer card header
    var subBoxTitleEl = section.querySelector('.productV8SubBoxHeaderTitle');
    if (subBoxTitleEl && offer && offer.dataset.label) subBoxTitleEl.textContent = offer.dataset.label;
    setHtml(section.querySelector('.productV8SubBoxHeaderServings'), servingsDescription);

    // v8: dynamic "Save X%" pill in the top price row
    var saveBadgeEl = section.querySelector('.productV8PriceSaveBadge');
    if (saveBadgeEl) {
      if (discountLabel) {
        saveBadgeEl.textContent = 'Save ' + discountLabel;
        saveBadgeEl.style.display = '';
      } else {
        saveBadgeEl.style.display = 'none';
      }
    }
    // v8: dynamic promo badge inside the offer card — uses {discount} placeholder
    var promoBadgeEl = section.querySelector('.productV8SubBoxPromoBadge');
    if (promoBadgeEl) {
      var tpl = promoBadgeEl.dataset.template || promoBadgeEl.textContent || '';
      if (!promoBadgeEl.dataset.template) promoBadgeEl.dataset.template = tpl;
      if (discountLabel) {
        promoBadgeEl.textContent = promoBadgeEl.dataset.template.replace('{discount}', discountLabel);
        promoBadgeEl.style.display = '';
      } else {
        promoBadgeEl.style.display = 'none';
      }
    }

    setText(section.querySelector('.productV8SubBoxHeaderDiscount'), discountLabel || 'Save');
    setText(section.querySelector('.productV8SaveBadge'), discountLabel);
    setText(section.querySelector('.productV8OtpPrice'), otpPriceText);
    setText(section.querySelector('.productV8OtpSupply'), otpSupplyText);
    setHtml(section.querySelector('.productV8SubRowDelivery'), deliveryText);
    setHtml(section.querySelector('.productV8SubServings'), servingsDescription);

    var badgeEl = section.querySelector('.productV8PriceBadge');
    if (badgeEl && servingsPer) badgeEl.textContent = 'As low as ' + servingsPer + ' a day';

    var atcPriceEl = section.querySelector('.productV8SubAtcPrice');
    if (atcPriceEl) atcPriceEl.textContent = isSub ? subPriceText : otpPriceText;
  }

  function applyVariant(section, variant) {
    if (!variant) return;

    switchGallery(section, String(variant.id));

    var variantInput = section.querySelector('.productV8VariantInput');
    if (variantInput) variantInput.value = variant.id;

    applyOfferAvailability(section, variant);
    applyOfferTexts(section, variant);

    // v8: top compare-at price prefers the active offer's OTP text. Falls back
    // to variant.compare_at_price (or hides) when no offer text is configured.
    var comparePriceEl = section.querySelector('.productV8ComparePrice');
    if (comparePriceEl) {
      var activeOffer = getActiveOffer(section);
      var compareText = (activeOffer && activeOffer.dataset.otpPriceText) || '';
      if (!compareText && variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareText = fmtMoney(variant.compare_at_price);
      }
      if (compareText) {
        comparePriceEl.textContent = compareText;
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    var atcBtn = section.querySelector('.productV8Atc');
    if (atcBtn) {
      var available = variant.available !== false;
      atcBtn.disabled = !available;
      setText(atcBtn.querySelector('.productV8AtcText'), available ? 'Add to Cart' : 'Sold Out');
    }

    var variantJsonEl = section.querySelector('.productV8VariantJSON');
    if (variantJsonEl) variantJsonEl.textContent = JSON.stringify(variant);

    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function updateFromSelections(section) {
    var productData = parseProductJSON(section);
    if (!productData) return;

    var flavorCard = section.querySelector('.productV8FlavorCard.is-selected');
    var variant = null;

    if (flavorCard) {
      var directVariantId = parseInt(flavorCard.dataset.variantId, 10);
      if (directVariantId) {
        variant = productData.variants.find(function (item) { return item.id === directVariantId; });
      }
      if (!variant) variant = resolveVariant(productData, flavorCard.dataset.flavorValue || '');
    }

    if (!variant) {
      var variantInput = section.querySelector('.productV8VariantInput');
      var currentId = variantInput ? parseInt(variantInput.value, 10) : null;
      if (currentId) {
        variant = productData.variants.find(function (item) { return item.id === currentId; });
      }
      if (!variant) variant = productData.variants.find(function (item) { return item.available; }) || productData.variants[0];
    }

    applyVariant(section, variant);
  }

  function initFlavorSelector(section) {
    var cards = section.querySelectorAll('.productV8FlavorCard');
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        cards.forEach(function (item) { item.classList.remove('is-selected'); });
        card.classList.add('is-selected');
        updateFromSelections(section);
      });
    });
  }

  function initOfferSelector(section) {
    var select = section.querySelector('.productV8OfferSelect');
    if (!select) return;
    select.addEventListener('change', function () {
      updateFromSelections(section);
    });
  }

  function initPurchaseToggle(section) {
    section.querySelectorAll('.productV8PurchaseRadio').forEach(function (radio) {
      radio.addEventListener('change', function () {
        section.querySelectorAll('.productV8PurchaseRow').forEach(function (row) {
          row.classList.remove('is-selected');
        });
        var row = radio.closest('.productV8PurchaseRow');
        if (row) row.classList.add('is-selected');
        updateFromSelections(section);
      });
    });
  }

  function initFaqAccordion(section) {
    section.querySelectorAll('.productV8FaqBtn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isActive = btn.classList.contains('is-active');
        section.querySelectorAll('.productV8FaqBtn').forEach(function (item) { item.classList.remove('is-active'); });
        if (!isActive) btn.classList.add('is-active');
      });
    });
  }

  function initLabelDialog(section) {
    var openBtn = section.querySelector('.productV8LabelBtn');
    var dialog = section.querySelector('.productV8LabelDialog');
    if (!openBtn || !dialog) return;

    var labelSwiper = null;
    if (typeof Swiper !== 'undefined') {
      var swiperEl = dialog.querySelector('.productV8LabelSwiper');
      if (swiperEl) {
        labelSwiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          autoHeight: true,
          navigation: {
            prevEl: dialog.querySelector('.productV8LabelNavBtn.swiper-button-prev'),
            nextEl: dialog.querySelector('.productV8LabelNavBtn.swiper-button-next'),
          },
        });
      }
    }

    openBtn.addEventListener('click', function () {
      if (labelSwiper) {
        var variantInput = section.querySelector('.productV8VariantInput');
        var currentId = variantInput ? variantInput.value : null;
        if (currentId) {
          var slides = Array.from(dialog.querySelectorAll('.swiper-slide[data-id]'));
          var idx = slides.findIndex(function (slide) { return slide.dataset.id === String(currentId); });
          if (idx >= 0) labelSwiper.slideTo(idx, 0);
        }
      }
      document.body.classList.add('noScroll');
      dialog.showModal();
    });

    var closeBtn = dialog.querySelector('.productV8LabelDialogClose');
    if (closeBtn) closeBtn.addEventListener('click', function () {
      dialog.close();
      document.body.classList.remove('noScroll');
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) {
        dialog.close();
        document.body.classList.remove('noScroll');
      }
    });

    dialog.addEventListener('close', function () {
      document.body.classList.remove('noScroll');
    });
  }

  function initAtcButton(section) {
    var atcBtn = section.querySelector('.productV8AtcSub');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', async function () {
      this.classList.add('loading');

      var hasSub = section.querySelector('.productV8PurchaseRadio[value="subscription"]:checked') !== null;
      var activeOffer = getActiveOffer(section);
      var planId = activeOffer ? (parseInt(activeOffer.value, 10) || null) : null;
      var quantity = activeOffer ? (parseInt(activeOffer.dataset.quantity, 10) || 1) : 1;
      var variantInput = section.querySelector('.productV8VariantInput');
      var variantId = variantInput ? parseInt(variantInput.value, 10) : null;

      // Keep whatever is already in the cart so different products (e.g. Adults + Kids) can
      // coexist. Each subscription brings its own welcome-kit gifts, tagged with _gift_for
      // so they belong to this product and can be removed with it. Skip re-adding gifts this
      // product already contributed (e.g. when clicking Add to cart again).
      var currentCart = await getCart();
      var ownerHasGifts = (currentCart.items || []).some(function (item) {
        return item.properties && item.properties._gift && String(item.properties._gift_for) === String(variantId);
      });

      var formData = [];

      if (typeof giftsV8 !== 'undefined' && !ownerHasGifts) {
        giftsV8.forEach(function (gift) {
          var giftId = parseInt(gift.id, 10);
          if (!giftId) return;
          if (hasSub || !gift.sub) {
            formData.push({ quantity: 1, id: giftId, properties: { _gift: true, _gift_for: String(variantId) } });
          }
        });
      }

      formData.push({
        quantity: quantity,
        id: variantId,
        selling_plan: hasSub ? planId : null,
        properties: hasSub && planId ? { _selling_plan_offer: planId } : {}
      });

      await addProduct({ items: formData });

      this.classList.remove('loading');
      await openCart();
    });
  }

  function initGiftDialogs(section) {
    section.querySelectorAll('.productV8SubPointLearnBtn').forEach(function (btn) {
      var item = btn.closest('.productV8SubPointItem');
      var dialog = item ? item.querySelector('.productV8SubPointLearnDialog') : null;
      if (!dialog) return;

      btn.addEventListener('click', function () { dialog.showModal(); });
      dialog.querySelectorAll('.productV8SubPointLearnClose').forEach(function (closeBtn) {
        closeBtn.addEventListener('click', function () { dialog.close(); });
      });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail && event.detail.sectionId === sectionId) init();
  });

  function init() {
    var section = getSection();
    if (!section) return;

    initGallerySwiper(section);
    initFlavorSelector(section);
    initOfferSelector(section);
    initPurchaseToggle(section);
    initAtcButton(section);
    initFaqAccordion(section);
    initLabelDialog(section);
    initGiftDialogs(section);
    updateFromSelections(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
